import { bookServiceApi } from '@/services/api';

// 1. TypeScript Interfaces

export interface Tag {
  tag_id: number;
  tag_name: string;
}

export interface BookTag {
  book_id: number;
  tag_id: number;
  tag_name: string;
  book_title: string;
}

// Items returned from GET /api/book-tags/tag/{tag_id}
export interface TagBookItem {
  book_id: number;
  title: string;
  author: string;
  cover_image_url?: string | null;
  average_rating?: number;
  tag_name?: string;
}

export interface TagBooksPagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface TagBooksResponse {
  books: TagBookItem[];
  pagination: TagBooksPagination;
}

export interface GetBooksByTagParams {
  page?: number;
  limit?: number;
}

// 2. API Function

/**
 * Fetches all books associated with a given tag with pagination.
 * @param tagId - The ID of the tag.
 * @param params - Pagination parameters.
 * @returns A promise that resolves to paginated books response.
 */
export const getBooksByTag = async (
  tagId: number,
  params: GetBooksByTagParams = {}
): Promise<TagBooksResponse> => {
  if (!tagId) {
    throw new Error('A tag ID must be provided.');
  }

  const { page = 1, limit = 20 } = params;

  try {
    const response = await bookServiceApi.get<TagBooksResponse>(
      `/book-tags/tag/${tagId}`,
      { page: String(page), limit: String(limit) }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch books by tag.');
  } catch (error) {
    console.error(`Error fetching books for tag ${tagId}:`, error);
    throw new Error('Failed to fetch books. Please try again later.');
  }
};

// 3. More TypeScript Interfaces for Create

export interface CreateTagPayload {
  tag_name: string;
}

export interface CreatedTag {
  tag_id: number;
  tag_name: string;
}

// 4. API Function for Create

/**
 * Creates a new tag.
 * @param payload - The data for the new tag.
 * @returns The newly created tag.
 */
export const createTag = async (payload: CreateTagPayload): Promise<CreatedTag> => {
  if (!payload.tag_name || payload.tag_name.trim() === '') {
    throw new Error('Tag name is required.');
  }

  try {
    const response = await bookServiceApi.post<CreatedTag>('/tags/', payload);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to create tag.');
  } catch (error: any) {
    console.error('Error creating tag:', error);
    throw error; // Re-throw the original error so the test runner can handle it properly
  }
};
